import React from 'react';

import { Spinner } from '@/components/ui/spinner';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { usePerScanAIBudget } from '@/hooks/usePerScanAIBudget';
import { PerScanAIBudgetEditForm } from '@/sections/settings/components/forms/PerScanAIBudgetEditForm';
import { SettingDisplayCard } from '@/sections/settings/components/SettingDisplayCard';
import { SettingEditableCard } from '@/sections/settings/components/SettingEditableCard';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';
import { formatUSD } from '@/utils/formatCurrency.util';

interface PerScanAIBudgetCardProps {
  email?: string;
  editable?: boolean;
}

const CONTENT_RIGHT_PADDING = 'pr-24';

/**
 * Per-scan AI budget — the max dollar spend a single AI-powered capability
 * scan (e.g. Constantine) may incur. Distinct from the monthly Bifrost cap
 * shown by AISpendCard. Budget value resolved via usePerScanAIBudget (single
 * source of truth); useMy is retained only for the loading status.
 */
export const PerScanAIBudgetCard: React.FC<PerScanAIBudgetCardProps> = ({
  email,
  editable = true,
}) => {
  const { isPraetorianUser } = useAuth();
  const { isSuperAdmin } = useEntitlements();
  const canEdit = editable && isSuperAdmin;
  const queryOptions = email ? { email, doNotImpersonate: true } : undefined;
  const { status } = useMy({ resource: 'configuration' }, queryOptions);
  const budget = usePerScanAIBudget({ email });

  if (status === 'pending') {
    return (
      <SettingDisplayCard data-testid="per-scan-ai-budget-card-loading">
        <h4 className="font-bold">Per-scan AI budget</h4>
        <Spinner centered size="xl" />
      </SettingDisplayCard>
    );
  }

  return (
    <SettingEditableCard
      data-testid="per-scan-ai-budget-card"
      editable={canEdit}
      lockedTooltip={
        editable && !isSuperAdmin
          ? 'Contact Nathan Sportsman, Peter Kwan, or Noah Tutt to update the AI budget'
          : undefined
      }
      editConfig={{
        modalTitle: 'Set per-scan AI budget',
        formComponent: PerScanAIBudgetEditForm,
        formProps: {
          email,
          currentBudget: budget,
        },
        modalSize: 'lg',
        strategy: 'local-state',
      }}
    >
      <div
        className={cn(
          'flex w-full flex-col items-start justify-between gap-6 sm:flex-row sm:items-center',
          CONTENT_RIGHT_PADDING
        )}
      >
        {/* LEFT: title + description */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h4 className="font-bold">Per-scan AI budget</h4>
          <span className="text-chariot-text-secondary text-xs">
            Max AI spend for a single scan (e.g. Constantine). Default $250.
          </span>
          {!isPraetorianUser && (
            <span className="text-chariot-text-secondary text-xs">
              Set by your Guard Intelligence tier.
            </span>
          )}
        </div>

        {/* RIGHT: promoted budget figure + caption */}
        <div className="flex shrink-0 flex-col items-start gap-1 text-right tabular-nums sm:items-end">
          <span
            className="text-chariot-text-primary text-lg font-semibold tabular-nums"
            data-testid="per-scan-ai-budget-value"
          >
            {formatUSD(budget)}
          </span>
          <span className="text-chariot-text-secondary text-xs">per scan</span>
        </div>
      </div>
    </SettingEditableCard>
  );
};
